import {
  BarChartOutlined,
  BarsOutlined,
  BgColorsOutlined,
  BuildOutlined,
  BulbOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  FieldNumberOutlined,
  HeatMapOutlined,
  HomeOutlined,
  MenuOutlined,
  PictureOutlined,
  ProductOutlined,
  ReadOutlined,
  TeamOutlined,
  UserOutlined,
  WechatOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Layout, Menu } from "antd";
import { Package, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { getCachedCurrentUser, loadCurrentUser } from "../auth/currentUser";
import { EditableColumnType, getIdColumn } from "../components/AdvancedTable";
import { PictureDataProvider } from "../contexts/PictureDataProvider";
import { LoadingTips } from "../shared/types/LoadingTips";
import { WitchGrade } from "../shared/types/WitchGrade";
import PageAssetBundle from "./PageAssetBundle";
import { PageErrorCode } from "./PageErrorCode";
import PageLibPictures from "./PageLibPictures";
import { PageMetaData, PageMetaDataProps } from "./PageMetaData";
import PagePropGraph from "./PagePropGraph";
import PagePropRuntimeSandbox from "../sandbox/pages/PagePropRuntimeSandbox";
import PagePropSimulation from "./PagePropSimulation";
import PageProps from "./PageProps";
import PageCharacters from "./PageCharacters";
import PageAnalytics from "./PageAnalytics";
import PageUserManagement from "./PageUserManagement";

const { Sider, Content } = Layout;

const MContent = styled(Content)`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
`;

const menus = [
  {
    key: "resources",
    label: "资源管理",
    icon: <ProductOutlined />,
    children: [
      { key: "pictures", label: "图库", icon: <PictureOutlined /> },
      { key: "assetbundle", label: "资源包", icon: <Package size={16} /> },
    ],
  },
  {
    key: "metadata",
    label: "数据管理",
    icon: <DatabaseOutlined />,
    children: [
      { key: "propsimulations", label: "模拟推演", icon: <BarChartOutlined /> },
      { key: "propgraphs", label: "炼金道具", icon: <BuildOutlined /> },
      //{ key: "materials", label: "炼金道具", icon: <BuildOutlined /> },
      { key: "witchgrades", label: "魔女段位", icon: <HeatMapOutlined /> },
      { key: "potions", label: "魔药", icon: <ExperimentOutlined /> },
      { key: "taskgroups", label: "委托库", icon: <BarsOutlined /> },
      { key: "taskdialogues", label: "委托对话", icon: <WechatOutlined /> },
      { key: "houses", label: "小屋", icon: <HomeOutlined /> },
      { key: "houseelements", label: "小屋元素", icon: <BgColorsOutlined /> },
      { key: "loadingtips", label: "加载文案", icon: <BulbOutlined /> },
      { key: "chapterstories", label: "章节故事", icon: <ReadOutlined /> },
    ],
  },
  { key: "analytics", label: "数据统计", icon: <BarChartOutlined /> },
  { key: "propruntimesandbox", label: "游戏沙盒", icon: <ExperimentOutlined /> },
  { key: "users", label: "用户管理", icon: <TeamOutlined /> },
  { key: "characters", label: "玩家数据", icon: <UserOutlined /> },
  { key: "errorcode", label: "错误码", icon: <ShieldAlert size={16} /> },
];

const ContainerLayout = styled(Layout)`
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const DesktopSider = styled(Sider)`
  flex: 0 0 200px !important;
  max-width: 200px !important;
  min-width: 200px !important;
  width: 200px !important;
  overflow: hidden;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavigationMenuScroll = styled.div`
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: #001529;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MainLayout = styled(Layout)`
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const MobileNavigation = styled.div`
  display: none;

  @media (max-width: 768px) {
    height: 48px;
    flex: 0 0 48px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    background: #001529;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
`;

const MobileMenuButton = styled(Button)`
  color: rgba(255, 255, 255, 0.88);

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
  }

  &:not(:disabled):active {
    color: #fff;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MobilePageTitle = styled.div`
  min-width: 0;
  flex: 1;
  color: rgba(255, 255, 255, 0.92);
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default function PageControl() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(getCachedCurrentUser());
  const [currentUserLoading, setCurrentUserLoading] = useState(!currentUser);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [openMenuKeys, setOpenMenuKeys] = useState<string[]>([]);
  const [menu, setMenu] = useState(
    location.pathname === "/control"
      ? "propgraphs"
      : location.pathname.split("/")[2],
  );
  const visibleMenus = menus.filter(
    item => item.key !== "users" || currentUser?.role === "superadmin",
  );
  const currentMenuLabel = visibleMenus
    .flatMap(item => [
      item,
      ...("children" in item && item.children ? item.children : []),
    ])
    .find(item => item.key === menu)?.label ?? "控制台";

  const handleMenuSelect = (key: string) => {
    setMenu(key);
    setNavigationOpen(false);
    navigate(`/control/${key}`);
  };

  const handleMenuOpenChange = (keys: string[]) => {
    const newlyOpenedKey = keys.find(key => !openMenuKeys.includes(key));
    setOpenMenuKeys(newlyOpenedKey ? [newlyOpenedKey] : []);
  };

  useEffect(() => {
    let mounted = true;
    void loadCurrentUser()
      .then(user => {
        if (mounted) setCurrentUser(user);
      })
      .catch(() => {
        if (mounted) setCurrentUser(undefined);
      })
      .finally(() => {
        if (mounted) setCurrentUserLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const nextMenu =
      location.pathname === "/control"
        ? "propgraphs"
        : location.pathname.split("/")[2];
    if (nextMenu) setMenu(nextMenu);
  }, [location.pathname]);

  return (
    <ContainerLayout>
      <DesktopSider>
        <NavigationMenuScroll>
          <Menu
            items={visibleMenus}
            theme="dark"
            mode="inline"
            openKeys={openMenuKeys}
            selectedKeys={[menu]}
            onOpenChange={handleMenuOpenChange}
            onSelect={({ key }) => handleMenuSelect(key)}
          />
        </NavigationMenuScroll>
      </DesktopSider>
      <MainLayout>
        <MobileNavigation>
          <MobileMenuButton
            type="text"
            icon={<MenuOutlined />}
            aria-label="打开导航"
            onClick={() => setNavigationOpen(true)}
          />
          <MobilePageTitle>{currentMenuLabel}</MobilePageTitle>
        </MobileNavigation>
        <MContent style={{ overflowX: "clip" }}>
          <PictureDataProvider>
            <Routes>
            <Route index element={<Navigate to="propgraphs" />} />
            <Route path="pictures" element={<PageLibPictures />} />
            <Route path="assetbundle" element={<PageAssetBundle />} />
            <Route path="props/*" element={<PageProps />} />
            <Route path="propgraphs" element={<PagePropGraph />} />
            <Route path="propruntimesandbox" element={<PagePropRuntimeSandbox />} />
            <Route path="propsimulations" element={<PagePropSimulation />} />
            <Route path="analytics" element={<PageAnalytics />} />
            <Route
              path="users"
              element={
                <PageUserManagement
                  currentUser={currentUser}
                  currentUserLoading={currentUserLoading}
                />
              }
            />
            <Route
              path="witchgrades"
              element={<PageMetaData<WitchGrade> {...pmdProps.witchgrades} />}
            />
            <Route
              path="materials"
              element={<PageMetaData<any> {...pmdProps.materials} />}
            />
            <Route
              path="potions"
              element={<PageMetaData<any> {...pmdProps.potions} />}
            />
            <Route
              path="taskgroups"
              element={<PageMetaData<any> {...pmdProps.taskgroups} />}
            />
            <Route
              path="taskdialogues"
              element={<PageMetaData<any> {...pmdProps.taskdialogues} />}
            />
            <Route
              path="houses"
              element={<PageMetaData<any> {...pmdProps.houses} />}
            />
            <Route
              path="houseelements"
              element={<PageMetaData<any> {...pmdProps.houseelements} />}
            />
            <Route
              path="loadingtips"
              element={<PageMetaData<LoadingTips> {...pmdProps.loadingtips} />}
            />
            <Route
              path="chapterstories"
              element={<PageMetaData<any> {...pmdProps.chapterstories} />}
            />
            <Route path="characters" element={<PageCharacters />} />
            <Route path="errorcode" element={<PageErrorCode />} />
            </Routes>
          </PictureDataProvider>
        </MContent>
      </MainLayout>
      <Drawer
        title="功能导航"
        placement="left"
        width="min(86vw, 320px)"
        open={navigationOpen}
        onClose={() => setNavigationOpen(false)}
        styles={{
          header: {
            color: "#fff",
            background: "#001529",
            borderBottomColor: "rgba(255,255,255,0.12)",
          },
          body: { padding: 0, background: "#001529" },
        }}
      >
        <NavigationMenuScroll>
          <Menu
            items={visibleMenus}
            theme="dark"
            mode="inline"
            openKeys={openMenuKeys}
            selectedKeys={[menu]}
            onOpenChange={handleMenuOpenChange}
            onSelect={({ key }) => handleMenuSelect(key)}
          />
        </NavigationMenuScroll>
      </Drawer>
    </ContainerLayout>
  );
}

const pmdProps: Record<string, PageMetaDataProps<any>> = {
  loadingtips: {
    title: "加载文案表",
    collection: "loadingtips",
    sheetIndex: 1,
    header: ["content", "duration"],
    columnStart: 1,
    columnEnd: 2,
    filedSchema: { duration: "number" },
    columns: [
      getIdColumn(),
      {
        title: "ID",
        dataIndex: "_id",
        width: 250,
      },
      {
        title: "内容",
        dataIndex: "content",
      },
      {
        title: "时长",
        dataIndex: "duration",
        width: 100,
        render: (v: number) => `${v}秒`,
      },
    ] as EditableColumnType<LoadingTips>[],
  },
  witchgrades: {
    title: "魔女段位数据表",
    collection: "witchgrades",
    sheetIndex: 1,
    header: [
      "grade",
      "level",
      "name",
      "image",
      "taskGroup",
      "materialStart",
      "makeTimes",
      "taskGoals",
      "taskRewards",
    ],
    columnEnd: 8,
    filedSchema: {
      grade: "number",
      level: "number",
      taskGroup: "string[]",
      materialStart: "string[]",
      makeTimes: "number[]",
      taskGoals: "number[]",
      taskRewards: "number[]",
    },
    columns: [
      getIdColumn(),
      {
        title: "魔女等级",
        dataIndex: "grade",
        width: 100,
      },
      {
        title: "魔女等级进度(分支)",
        dataIndex: "level",
        width: 100,
      },
      {
        title: "魔女等级文本",
        dataIndex: "name",
        width: 100,
      },
      {
        title: "魔女形象解锁",
        dataIndex: "image",
        width: 100,
      },
      {
        title: "魔女委托库",
        dataIndex: "taskGroup",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
      {
        title: "初始材料",
        dataIndex: "materialStart",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
      {
        title: "魔药制作次数序列",
        dataIndex: "makeTimes",
        width: 100,
        render: (v: number[]) => v?.join(", "),
      },
      {
        title: "委托要求序列",
        dataIndex: "taskGoals",
        width: 100,
        render: (v: number[]) => v?.join(", "),
      },
      {
        title: "委托出售基础奖励",
        dataIndex: "taskRewards",
        width: 100,
        render: (v: number[]) => v?.join(", "),
      },
    ],
  },
  potions: {
    title: "魔药数据表",
    collection: "potions",
    sheetIndex: 1,
    header: [
      "type",
      "typeDescription",
      "potionId",
      "name",
      "description",
      "icon",
      "color",
    ],
    columnEnd: 6,
    columns: [
      getIdColumn(),
      {
        title: "类别",
        dataIndex: "type",
        width: 100,
      },
      {
        title: "类别说明",
        dataIndex: "typeDescription",
        width: 100,
      },
      {
        title: "魔药ID",
        dataIndex: "potionId",
        width: 250,
      },
      {
        title: "魔药名称",
        dataIndex: "name",
        width: 200,
      },
      {
        title: "魔药说明",
        dataIndex: "description",
      },
      {
        title: "魔药标志资源",
        dataIndex: "icon",
        width: 100,
      },
      {
        title: "魔药色值",
        dataIndex: "color",
        width: 100,
      },
    ],
  },
  taskgroups: {
    title: "委托库数据表",
    collection: "taskgroups",
    sheetIndex: 1,
    header: ["groupId", "taskId", "description", "taskDialogueId", "submits"],
    columnEnd: 4,
    filedSchema: { submits: "string[]" },
    columns: [
      getIdColumn(),
      {
        title: "委托库ID",
        dataIndex: "groupId",
        width: 100,
      },
      {
        title: "委托唯一ID",
        dataIndex: "taskId",
        width: 100,
      },
      {
        title: "委托说明",
        dataIndex: "description",
      },
      {
        title: "委托文案对话表ID",
        dataIndex: "taskDialogueId",
      },
      {
        title: "交付魔药库",
        dataIndex: "submits",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
    ],
  },
  taskdialogues: {
    title: "委托对话数据表",
    collection: "taskdialogues",
    sheetIndex: 2,
    header: [
      "dialogueId",
      "image",
      "npcAction",
      "roleName",
      "roleDescription",
      "imagePosition",
      "content",
      "duration",
      "nextId",
    ],
    columnEnd: 8,
    filedSchema: { duration: "number" },
    columns: [
      getIdColumn(),
      {
        title: "对话ID",
        dataIndex: "dialogueId",
        width: 150,
      },
      {
        title: "对话形象",
        dataIndex: "image",
        width: 200,
      },
      {
        title: "NPC动作",
        dataIndex: "npcAction",
        width: 200,
      },
      {
        title: "角色名称",
        dataIndex: "roleName",
        width: 150,
      },
      {
        title: "形象备注",
        dataIndex: "roleDescription",
        width: 150,
      },
      {
        title: "形象位置",
        dataIndex: "imagePosition",
        width: 100,
      },
      {
        title: "对话内容",
        dataIndex: "content",
      },
      {
        title: "对话展示时长",
        dataIndex: "duration",
        width: 100,
        render: (v: number) => `${v}毫秒`,
      },
      {
        title: "下一句对话",
        dataIndex: "nextId",
        width: 150,
      },
    ],
  },
  houses: {
    title: "小屋描述表",
    collection: "houses",
    sheetIndex: 2,
    header: ["houseId", "index", "name", "icon", "image", "description"],
    columnEnd: 5,
    filedSchema: { index: "number" },
    columns: [
      getIdColumn(),
      {
        title: "小屋ID",
        dataIndex: "houseId",
        width: 100,
      },
      {
        title: "小屋序列",
        dataIndex: "index",
        width: 100,
      },
      {
        title: "所属小屋",
        dataIndex: "name",
        width: 100,
      },
      {
        title: "小屋的ICON",
        dataIndex: "icon",
        width: 100,
      },
      {
        title: "小屋的宣传图",
        dataIndex: "image",
        width: 150,
      },
      {
        title: "小屋的说明",
        dataIndex: "description",
      },
    ],
  },
  houseelements: {
    title: "小屋元素表",
    collection: "houseelements",
    sheetIndex: 1,
    header: [
      "houseId",
      "houseName",
      "isCount",
      "assetId",
      "icon",
      "name",
      "description",
      "pre",
      "mutex",
      "price",
      "unlock",
    ],
    columnEnd: 10,
    filedSchema: { isCount: "number", price: "number" },
    columns: [
      getIdColumn(),
      {
        title: "小屋ID",
        dataIndex: "houseId",
        width: 100,
      },
      {
        title: "所属小屋",
        dataIndex: "houseName",
        width: 100,
      },
      {
        title: "是否计数",
        dataIndex: "isCount",
        width: 100,
      },
      {
        title: "SPINE资源ID",
        dataIndex: "assetId",
        width: 150,
      },
      {
        title: "所属设施的ICON图",
        dataIndex: "icon",
        width: 150,
      },
      {
        title: "所属设施的中文名",
        dataIndex: "name",
        width: 150,
      },
      {
        title: "设施备注",
        dataIndex: "description",
      },
      {
        title: "前置设置和设施等级",
        dataIndex: "pre",
        width: 150,
      },
      {
        title: "互斥设施",
        dataIndex: "mutex",
        width: 150,
      },
      {
        title: "消耗/费用",
        dataIndex: "price",
        width: 100,
      },
      {
        title: "解锁",
        dataIndex: "unlock",
        width: 100,
      },
    ],
  },
  chapterstories: {
    title: "章节故事表",
    collection: "chapterstories",
    sheetIndex: 1,
    header: ["chapter", "index", "content", "asset", "position", "button"],
    columnEnd: 5,
    filedSchema: { chapter: "number", index: "number" },
    columns: [
      getIdColumn(),
      {
        title: "所属章节",
        dataIndex: "chapter",
        width: 100,
      },
      {
        title: "章节序号",
        dataIndex: "index",
        width: 100,
      },
      {
        title: "文本内容",
        dataIndex: "content",
      },
      {
        title: "插图美术资源",
        dataIndex: "asset",
        width: 150,
      },
      {
        title: "插图位置",
        dataIndex: "position",
        width: 100,
      },
      {
        title: "交互按钮",
        dataIndex: "button",
        width: 100,
      },
    ],
  },
  materials: {
    title: "炼金道具表",
    collection: "materials",
    sheetIndex: 0,
    header: [
      "materialId",
      "icon",
      "spine",
      "copy",
      "name",
      "description",
      "tips",
      "rarity",
      "type",
      "health",
      "cost",
      "score",
      "extra",
      "add",
      "addChance",
      "remove",
      "removeType",
      "removeScore",
      "removeSelf",
      "removeSelfChance",
      "removeSelfScore",
      "replace",
      "replaceChance",
      "replaceScore",
      "exist",
      "existMode",
      "existScore",
      "special",
    ],
    columnStart: 0,
    columnEnd: 27,
    filedSchema: {
      tips: "string[]",
      rarity: "number",
      type: "number",
      health: "number",
      cost: "number",
      score: "number",
      extra: "number",
      addChance: "number",
      remove: "string[]",
      removeType: "number",
      removeScore: "number",
      removeSelf: "string[]",
      removeSelfChance: "number",
      removeSelfScore: "number",
      replace: "string[]",
      replaceChance: "number",
      replaceScore: "number",
      exist: "string[]",
      existMode: "number",
      existScore: "number",
    },
    columns: [
      getIdColumn(),
      { title: "道具ID", dataIndex: "materialId", width: 100 },
      { title: "道具ICON", dataIndex: "icon", width: 100 },
      { title: "对标道具", dataIndex: "copy", width: 100 },
      { title: "道具SPINE", dataIndex: "spine", width: 100 },
      { title: "道具名称", dataIndex: "name", width: 100 },
      { title: "道具说明", dataIndex: "description", width: 100 },
      {
        title: "题材词条",
        dataIndex: "tips",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
      { title: "道具的品质", dataIndex: "rarity", width: 100 },
      { title: "道具类型", dataIndex: "type", width: 100 },
      { title: "道具生命", dataIndex: "health", width: 100 },
      { title: "道具消耗", dataIndex: "cost", width: 100 },
      { title: "道具基础得分", dataIndex: "score", width: 100 },
      { title: "道具加成得分", dataIndex: "extra", width: 100 },
      { title: "增加", dataIndex: "add", width: 100 },
      { title: "增加概率", dataIndex: "addChance", width: 100 },
      {
        title: "道具吞噬",
        dataIndex: "remove",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
      { title: "吞噬方式", dataIndex: "removeType", width: 100 },
      { title: "吞噬得分", dataIndex: "removeScore", width: 100 },
      {
        title: "自身异变",
        dataIndex: "removeSelf",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
      { title: "异变概率", dataIndex: "removeSelfChance", width: 100 },
      { title: "异变得分", dataIndex: "removeSelfScore", width: 100 },
      {
        title: "消亡异变",
        dataIndex: "replace",
        width: 100,
        render: (v: string[]) => v?.join(", "),
      },
      { title: "异变概率", dataIndex: "replaceChance", width: 100 },
      { title: "异变得分", dataIndex: "replaceSocre", width: 100 },
      { title: "同时存在", dataIndex: "exist", width: 100 },
      { title: "存在方式", dataIndex: "existMode", width: 100 },
      { title: "存在得分", dataIndex: "existScore", width: 100 },
      { title: "特殊机制", dataIndex: "special", width: 100 },
    ],
  },
};
